import { useState, useEffect, useRef } from "react";
import PortalConfetti from "./PortalConfetti";
import { doc, setDoc, getDoc, increment } from "firebase/firestore";
import { db } from "../firebase";
import { MUSIC_MAP } from "../maps";
import { MUSIC } from "../data";
import { getDailyItem, updateGameStats } from "../utils/gameLogic";
import { usePersistentStorage, useKeyboardNavigation } from "../hooks/useGameHooks";

/**
 * Music (Heardle-style) guessing mode — 6 guesses with progressively longer audio clips.
 * Players choose between "Play From Beginning" or "Play Random Part" at the start.
 * Progressive hints reveal track type (after 2 wrong guesses) and game (after 4).
 *
 * @param {{ version: string, targetDateObj: Date, targetDateStr: string, isArchive: boolean, setIsModalOpen: (v: boolean) => void, guessedTracks: Array<Object>, setGuessedTracks: (g: Array<Object>) => void }} props
 * @param {string} props.version - "old" or "new".
 * @param {Date} props.targetDateObj - Target date object.
 * @param {string} props.targetDateStr - Date string for localStorage keys.
 * @param {boolean} props.isArchive - Whether viewing an archived date.
 * @param {(v: boolean) => void} props.setIsModalOpen - Opens global stats modal.
 * @param {Array<Object>} props.guessedTracks - Previously submitted track guesses.
 * @param {(g: Array<Object>) => void} props.setGuessedTracks - Guess state setter.
 * @returns {JSX.Element} The music game UI.
 */
export default function MusicMode({
  version,
  targetDateObj,
  targetDateStr,
  isArchive,
  setIsModalOpen,
  guessedTracks,
  setGuessedTracks,
}) {
  const [currentGuess, setCurrentGuess] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [collection, setCollection] = usePersistentStorage(
    "kisekidle-collection-music",
    []
  );
  const [isCopied, setIsCopied] = useState(false);

  const [targetTrack] = useState(getDailyItem(MUSIC, targetDateObj, MUSIC_MAP));

  const MAX_GUESSES = 6;
  const wrongGuessesCount = guessedTracks.filter(
    (track) => track.id !== targetTrack.id
  ).length;
  const hasWon = guessedTracks.some((track) => track.id === targetTrack.id);
  const guessesLeft = MAX_GUESSES - guessedTracks.length;
  const isGameOver = guessesLeft <= 0 && !hasWon;

  /** Processes a track guess — checks win, updates stats, adds to collection, stops audio on win. */
  const handleGuess = (selectedTrack) => {
    if (hasWon || isGameOver) return;
    if (guessedTracks.find((t) => t.id === selectedTrack.id)) return;

    const newGuesses = [...guessedTracks, selectedTrack];
    const isWin = selectedTrack.id === targetTrack.id;
    const isLoss = newGuesses.length >= MAX_GUESSES && !isWin;

    if (!isArchive && (isWin || isLoss)) {
      if (typeof updateGameStats === "function") {
        updateGameStats("music", isWin, newGuesses.length, MAX_GUESSES);
      }
    }

    if (isWin) {
      const selectedId = String(selectedTrack.id);
      if (!collection.some((id) => String(id) === selectedId)) {
        setCollection([...collection, selectedId]);
      }
    }

    setGuessedTracks(newGuesses);
    setCurrentGuess("");
    setSuggestions([]);

    if (isWin) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const { focusedIndex, setFocusedIndex, handleKeyDown, listRef } =
    useKeyboardNavigation(suggestions, handleGuess);

  // --- ANTI-CHEAT WIN FLAG ---
  useEffect(() => {
    if (hasWon) {
      const storageKey = `kisekidle-music-${targetDateStr}`;
      const storedData = JSON.parse(localStorage.getItem(storageKey) || "{}");
      storedData.hasWon = true;
      localStorage.setItem(storageKey, JSON.stringify(storedData));
    }
  }, [targetDateStr, hasWon]);

  const TIME_LIMITS = [1, 2, 5, 10, 30, 9999];
  const currentAllowedTime =
    TIME_LIMITS[Math.min(guessedTracks.length, TIME_LIMITS.length - 1)];

  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.5);

  const [playbackConfig, setPlaybackConfig] = useState(() => {
    const stored = localStorage.getItem(`kisekidle-music-config-${targetDateStr}`);
    return stored ? JSON.parse(stored) : null;
  });
  const [isAudioLoaded, setIsAudioLoaded] = useState(false);

  /** Persists the user's playback mode choice (beginning vs random start) for the day. */
  const handleChooseMode = (mode) => {
    let config = { mode };
    if (mode === "random") {
      let duration = audioRef.current?.duration;
      if (!duration || isNaN(duration)) {
        alert("Track is loading, please try again in a moment...");
        return;
      }
      const maxStart = Math.max(0, duration - 30);
      config.startTime = Math.random() * maxStart;
    } else {
      config.startTime = 0;
    }
    setPlaybackConfig(config);
    localStorage.setItem(`kisekidle-music-config-${targetDateStr}`, JSON.stringify(config));
  };

  /** Toggles audio playback — resets to the configured start time on each play. */
  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.currentTime = playbackConfig?.startTime || 0;
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  /** Adjusts audio volume from the slider input. */
  const handleVolumeChange = (e) => {
    const newVol = parseFloat(e.target.value);
    setVolume(newVol);
    if (audioRef.current) {
      audioRef.current.volume = newVol;
    }
  };

  /** Pauses playback when the current clip time limit (based on guess count) is reached. */
  const handleTimeUpdate = (e) => {
    const start = playbackConfig?.startTime || 0;
    if (e.target.currentTime >= start + currentAllowedTime && !hasWon) {
      e.target.pause();
      e.target.currentTime = start;
      setIsPlaying(false);
    }
  };

  const [isGameRevealed, setIsGameRevealed] = useState(false);
  const [isTypeRevealed, setIsTypeRevealed] = useState(false);
  const guessesNeededForType = Math.max(0, 2 - wrongGuessesCount);
  const guessesNeededForGame = Math.max(0, 4 - wrongGuessesCount);

  const [globalStats, setGlobalStats] = useState(null);

  /** Filters music tracks by title, display title, or game name, excluding already-guessed ones. */
  const handleInputChange = (e) => {
    const value = e.target.value;
    setCurrentGuess(value);
    setFocusedIndex(-1);
    if (value.length > 0) {
      const query = value.toLowerCase().trim();
      const filteredTracks = MUSIC.filter((track) => {
        const matchTitle = track.title.toLowerCase().includes(query);
        const matchDisplay = track.displayTitle.toLowerCase().includes(query);
        const matchGame = track.game.toLowerCase().includes(query);
        return (matchTitle || matchDisplay || matchGame) &&
          !guessedTracks.some((g) => g.id === track.id);
      }).sort((a, b) => {
        const titleA = a.title.toLowerCase();
        const displayA = a.displayTitle.toLowerCase();
        const gameA = a.game.toLowerCase();

        const titleB = b.title.toLowerCase();
        const displayB = b.displayTitle.toLowerCase();
        const gameB = b.game.toLowerCase();

        const aTitleStarts =
          titleA.startsWith(query) || displayA.startsWith(query);
        const bTitleStarts =
          titleB.startsWith(query) || displayB.startsWith(query);

        if (aTitleStarts && !bTitleStarts) return -1;
        if (!aTitleStarts && bTitleStarts) return 1;

        const aGameStart = gameA.startsWith(query);
        const bGameStart = gameB.startsWith(query);

        if (aGameStart && !bGameStart) return -1;
        if (!aGameStart && bGameStart) return 1;

        return titleA.localeCompare(titleB);
      });
      setSuggestions(filteredTracks.slice(0, 15));
    } else {
      setSuggestions([]);
    }
  };

  const syncInProgress = useRef(false);

  // --- MUSIC MODE TRACKING ---
  useEffect(() => {
    const syncReceipt = `synced-music-${targetDateStr}`;
    const hasAlreadySynced = localStorage.getItem(syncReceipt);

    if ((hasWon || isGameOver) && !hasAlreadySynced && !syncInProgress.current) {
      const updateAndFetchStats = async () => {
        syncInProgress.current = true;
        try {
          const statsRef = doc(db, "music_stats", targetDateStr);
          const updateData = {
            totalPlays: increment(1),
            totalWins: hasWon ? increment(1) : increment(0),
            totalWinningAttempts: hasWon
              ? increment(guessedTracks.length)
              : increment(0),
            guessTally: {},
          };
          guessedTracks.forEach((track) => {
            if (track && track.title)
              updateData.guessTally[track.title] = increment(1);
          });
          await setDoc(statsRef, updateData, { merge: true });
          localStorage.setItem(syncReceipt, "true");
          const updatedDoc = await getDoc(statsRef);
          if (updatedDoc.exists()) setGlobalStats(updatedDoc.data());
        } catch (error) {
          console.error("Firebase Error:", error);
        } finally {
          syncInProgress.current = false;
        }
      };
      updateAndFetchStats();
    }
  }, [hasWon, isGameOver, guessedTracks.length, targetDateStr]);

  /** Copies music-mode results (emoji or text format) to clipboard. */
  const handleShare = (format) => {
    const score = hasWon ? guessedTracks.length : "X";
    let shareText = `Kisekidle - Music (${targetDateStr})\nGuesses: ${score}/${MAX_GUESSES}\n\n`;
    
    if (format === "emoji") {
      const emojis = guessedTracks
        .map((track) => (track.id === targetTrack.id ? "🟩" : "🟥"))
        .join("");
      shareText += emojis;
    } else {
      guessedTracks.forEach((track, i) => {
        shareText += `${i + 1}. ${track.title} ${track.id === targetTrack.id ? "✅" : "❌"}\n`;
      });
    }

    shareText += `\n\nhttps://kisekidle.app/`;

    navigator.clipboard.writeText(shareText).then(() => {
      setIsCopied(format);
      setTimeout(() => setIsCopied(null), 2000);
    });
  };

  return (
    <div className="game-terminal music-terminal">
      <div className="header-container">
        <h2 className="mode-title">Listen to the Track</h2>
        <div className="guesses-remaining">
          Guesses Remaining: {guessesLeft}
        </div>
      </div>

      <audio
        ref={audioRef}
        src={targetTrack.audioUrl}
        onEnded={() => setIsPlaying(false)}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={() => setIsAudioLoaded(true)}
      />

      <div className="player-panel">
        <div style={{ fontSize: "4.5rem", marginBottom: "10px", filter: "drop-shadow(0 0 10px rgba(77, 136, 255, 0.3))" }}>
          {isPlaying ? "🎶" : "🎵"}
        </div>
        <p className="playback-info">
          Unlocked:{" "}
          <span className="unlocked-time">
            {currentAllowedTime === 9999
              ? "Full Track"
              : `${currentAllowedTime} seconds`}
          </span>
        </p>

        {!playbackConfig ? (
          <div className="playback-choice">
            <p className="choice-hint">
              Choose your mode for today's puzzle.<br />
              <span style={{ fontSize: "12px", opacity: 0.8 }}>(This cannot be changed until tomorrow!)</span>
            </p>
            <button
              className="submit-button mode-beginning premium-hover"
              onClick={() => handleChooseMode("beginning")}
            >
              ▶️ Play From Beginning
            </button>
            <button
              className="submit-button mode-random premium-hover"
              onClick={() => handleChooseMode("random")}
              disabled={!isAudioLoaded}
            >
              {isAudioLoaded ? "🎲 Play Random Part" : "⏳ Loading Track..."}
            </button>
          </div>
        ) : (
          <button
            className={`submit-button premium-hover ${isPlaying ? "playing" : "paused"}`}
            onClick={togglePlay}
          >
            {isPlaying ? "⏹️ Pause Track" : "▶️ Play Track"}
          </button>
        )}
        <div className="volume-container">
          <span>🔈</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={handleVolumeChange}
            className="volume-slider"
          />
          <span>🔊</span>
        </div>
      </div>

      {hasWon && (
        <div className="victory-box">
          <PortalConfetti
            width={window.innerWidth}
            height={window.innerHeight}
            recycle={false}
            numberOfPieces={400}
            gravity={0.15}
          />
          <h3>🎉 You got it! 🎉</h3>
          <p>
            The track was <strong>{targetTrack.title}</strong>.
          </p>
          <div className="share-buttons-container">
            <button
              onClick={() => setIsModalOpen(true)}
              className="share-all-btn global-stats-trigger premium-hover"
            >
              📊 View Global Stats
            </button>

            <button className="share-all-btn premium-hover" onClick={() => handleShare("emoji")}>
              {isCopied === "emoji" ? "📋 Copied!" : "📤 Share Emojis"}
            </button>

            <button className="share-all-btn premium-hover" onClick={() => handleShare("text")}>
              {isCopied === "text" ? "📋 Copied!" : "📤 Share Text"}
            </button>
          </div>
        </div>
      )}

      {isGameOver && (
        <div className="game-over-box">
          <h3>❌ Out of guesses! ❌</h3>
          <p>
            The correct track was <strong>{targetTrack.title}</strong>.
          </p>
          <div className="share-buttons-container">
            <button
              onClick={() => setIsModalOpen(true)}
              className="share-all-btn global-stats-trigger premium-hover"
            >
              📊 View Global Stats
            </button>

            <button className="share-all-btn premium-hover" onClick={() => handleShare("emoji")}>
              {isCopied === "emoji" ? "📋 Copied!" : "📤 Share Emojis"}
            </button>

            <button className="share-all-btn premium-hover" onClick={() => handleShare("text")}>
              {isCopied === "text" ? "📋 Copied!" : "📤 Share Text"}
            </button>
          </div>
        </div>
      )}

      <div className="hints-container">
        <div className="hint-wrapper">
          <button
            className={`hint-btn premium-hover ${isTypeRevealed || hasWon || isGameOver
              ? "revealed"
              : guessesNeededForType <= 0
                ? "unlocked"
                : "locked"
              }`}
            onClick={() => guessesNeededForType <= 0 && setIsTypeRevealed(true)}
            disabled={
              guessesNeededForType > 0 || isTypeRevealed || hasWon || isGameOver
            }
          >
            {isTypeRevealed || hasWon || isGameOver
              ? `Type: ${targetTrack.type}`
              : "Reveal Type"}
          </button>

          {!(hasWon || isGameOver) && (
            <div className="hint-condition">
              {guessesNeededForType > 0
                ? `Available after ${guessesNeededForType} more guess${guessesNeededForType > 1 ? "es" : ""
                }`
                : "Unlocked!"}
            </div>
          )}
        </div>

        <div className="hint-wrapper">
          <button
            className={`hint-btn premium-hover ${isGameRevealed || hasWon || isGameOver
              ? "revealed"
              : guessesNeededForGame <= 0
                ? "unlocked"
                : "locked"
              }`}
            onClick={() => guessesNeededForGame <= 0 && setIsGameRevealed(true)}
            disabled={
              guessesNeededForGame > 0 || isGameRevealed || hasWon || isGameOver
            }
          >
            {isGameRevealed || hasWon || isGameOver
              ? `Game: ${targetTrack.game}`
              : "Reveal Game"}
          </button>

          {!(hasWon || isGameOver) && (
            <div className="hint-condition">
              {guessesNeededForGame > 0
                ? `Available after ${guessesNeededForGame} more guess${guessesNeededForGame > 1 ? "es" : ""
                }`
                : "Unlocked!"}
            </div>
          )}
        </div>
      </div>

      {!hasWon && !isGameOver && (
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            value={currentGuess}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Guess the track title..."
            autoFocus
          />
          <button
            className="submit-button premium-hover"
            onClick={() =>
              suggestions.length > 0 && handleGuess(suggestions[0])
            }
          >
            Submit Guess
          </button>
          {suggestions.length > 0 && (
            <ul className="suggestions-list" ref={listRef}>
              {suggestions.map((track, index) => (
                <li
                  key={track.id}
                  className={index === focusedIndex ? "focused" : ""}
                  onClick={() => handleGuess(track)}
                  onMouseEnter={() => setFocusedIndex(index)}
                >
                  {track.displayTitle}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <ul className="guess-list">
        {guessedTracks
          .slice()
          .reverse()
          .map((track, index) => {
            const isCorrect = track.id === targetTrack.id;
            return (
              <li
                key={index}
                className={`guess-item ${isCorrect ? "correct" : "incorrect"}`}
              >
                <strong>{track.title}</strong>
                <span>{isCorrect ? "🎉 Correct!" : "❌ Incorrect"}</span>
              </li>
            );
          })}
      </ul>
    </div>
  );
}